import React from "react";
import { Form, Row, Col, Typography, Select, Button, Card } from "antd";
import {
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ImageUpload from "./ImageUpload";

// Banner types that carry a click-through link rather than pointing at a show.
const LINKED_BANNER_TYPES = ["generic", "promotion"];

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * The banner's artwork, one image set per language; a viewer on a language with
 * no set of its own is served the English set (lang 1).
 *
 * Sets are stored in m_images_lang under show_type = 'banner' and travel on the
 * payload as `images`. Held on the form through Form.List rather than in page
 * state, so the language pickers validate with every other field and the array
 * arrives on `values.images` already shaped for the API.
 */
const BannerLangImages = ({
  form,
  languages,
  required = false,
  bannerType,
}) => {
  // Gates all three link-banner-only uploads: a show banner serves the show's own
  // crops through banner_info, so a crop uploaded here would never be read.
  const needsHorizontalCard = LINKED_BANNER_TYPES.includes(bannerType);
  const imageColSpan = needsHorizontalCard ? 8 : 12;

  const languageList = Array.isArray(languages)
    ? languages
    : languages?.data || [];

  // Watched, not read once: the uploads render live values, and a language taken
  // by another set is disabled so two sets for one language cannot be saved.
  const imageSets = Form.useWatch("images", form) || [];
  const languageTakenBy = (langId, index) =>
    imageSets.some(
      (set, i) =>
        i !== index && set && Number(set.lang_id) === Number(langId)
    );

  const emptyImageSet = {
    lang_id: undefined,
    banner_image: "",
    card_image: "",
    horizontal_movie_card_image: "",
    horizontal_sleek_banner_image: "",
    vertical_card_image: "",
  };

  return (
    <div
      className="bg-white p-6 rounded-lg border border-gray-200"
      style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
    >
      <Form.List name="images">
        {(fields, { add, remove }) => (
          <>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <GlobalOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Banner Images
                </Title>
              </div>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add(emptyImageSet)}
              >
                Add Image Set
              </Button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                One set per language. Viewers on a language with no set of its
                own are served the English set. Shown only in the countries
                selected in Configuration.
              </Text>
            </div>

            {fields.length === 0 && (
              <Card
                style={{
                  textAlign: "center",
                  padding: "24px",
                  background: "#fafafa",
                }}
              >
                <Text type="secondary">
                  No image sets added yet. Click "Add Image Set" to upload
                  artwork for a language.
                </Text>
              </Card>
            )}

            {fields.map(({ key, name }, index) => (
              <Card
                key={key}
                style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                extra={
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    style={{ fontWeight: 600 }}
                  >
                    Remove
                  </Button>
                }
              >
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Image Language
                    </span>
                  }
                  name={[name, "lang_id"]}
                  rules={[
                    { required: true, message: "Please select language" },
                  ]}
                >
                  <Select
                    placeholder="Select image language"
                    className="rounded-lg"
                    showSearch
                    optionFilterProp="children"
                  >
                    {languageList.map((language) => (
                      <Option
                        key={language.lang_id}
                        value={language.lang_id}
                        // Disabled rather than hidden, so a language taken by
                        // another set reads as taken rather than missing.
                        disabled={languageTakenBy(language.lang_id, index)}
                      >
                        {/* option_label carries the "(Inactive)" suffix for a
                            language switched off after this set was uploaded. */}
                        {language.option_label || language.lang_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={imageColSpan}>
                    <ImageUpload
                      label="Desktop Banner Image"
                      name={[name, "banner_image"]}
                      width={1280}
                      height={720}
                      imageType="banners"
                      required={required}
                      allowGif
                      minGifSize
                      value={imageSets[index]?.banner_image || ""}
                      onChange={(val) =>
                        form.setFieldValue(
                          ["images", name, "banner_image"],
                          val
                        )
                      }
                    />
                  </Col>
                  <Col xs={24} lg={imageColSpan}>
                    <ImageUpload
                      label="Mobile Banner Image"
                      name={[name, "card_image"]}
                      width={360}
                      height={600}
                      imageType="banners"
                      required={required}
                      allowGif
                      minGifSize
                      value={imageSets[index]?.card_image || ""}
                      onChange={(val) =>
                        form.setFieldValue(["images", name, "card_image"], val)
                      }
                    />
                  </Col>
                  {/* 16:9 thumbnail for the carousel strip. Optional: the
                      desktop artwork stands in when it is not uploaded. Hidden
                      for a show banner, which uses its show's own thumbnail. */}
                  {needsHorizontalCard && (
                    <Col xs={24} lg={8}>
                      <ImageUpload
                        label="Horizontal Card Image"
                        name={[name, "horizontal_movie_card_image"]}
                        width={320}
                        height={180}
                        imageType="banners"
                        allowGif
                        minGifSize
                        value={
                          imageSets[index]?.horizontal_movie_card_image || ""
                        }
                        onChange={(val) =>
                          form.setFieldValue(
                            ["images", name, "horizontal_movie_card_image"],
                            val
                          )
                        }
                      />
                    </Col>
                  )}
                  {/* The sleek strip's crop: the same 180 height as the
                      horizontal card, half again as wide. Its own upload rather
                      than a reuse of that one — 8:3 cannot be cropped out of
                      16:9 without losing height. Same banner types, since it is
                      a variant of the same artwork. */}
                  {needsHorizontalCard && (
                    <Col xs={24} lg={8}>
                      <ImageUpload
                        label="Horizontal Sleek Banner Image"
                        name={[name, "horizontal_sleek_banner_image"]}
                        width={480}
                        height={180}
                        imageType="banners"
                        allowGif
                        minGifSize
                        value={
                          imageSets[index]?.horizontal_sleek_banner_image || ""
                        }
                        onChange={(val) =>
                          form.setFieldValue(
                            ["images", name, "horizontal_sleek_banner_image"],
                            val
                          )
                        }
                      />
                    </Col>
                  )}
                  {/* The portrait poster: 360x600, the size vertical_card_image
                      is filled at everywhere else in the dashboard. The same
                      shape as the mobile crop but its own upload, because the
                      two run in different slots at once — the mobile banner
                      across the top of a phone, the poster in a vertical rail. */}
                  {needsHorizontalCard && (
                    <Col xs={24} lg={8}>
                      <ImageUpload
                        label="Vertical Card Image"
                        name={[name, "vertical_card_image"]}
                        width={360}
                        height={600}
                        imageType="banners"
                        allowGif
                        minGifSize
                        value={imageSets[index]?.vertical_card_image || ""}
                        onChange={(val) =>
                          form.setFieldValue(
                            ["images", name, "vertical_card_image"],
                            val
                          )
                        }
                      />
                    </Col>
                  )}
                </Row>
              </Card>
            ))}
          </>
        )}
      </Form.List>

      {/* The redirect link lives in Configuration now — see BannerForm. */}
    </div>
  );
};

/**
 * The banner payload, from the form's own values. The artwork fields are plain
 * columns on the banner, so the form field names are the API field names — this
 * only has to settle what a type change means for the show fields.
 */
export const buildBannerPayload = (values) => {
  const payload = { ...(values || {}) };

  // The show fields are only rendered for a `show` banner, and antd omits an
  // unmounted field from the submitted values — so switching a banner to generic
  // would send nothing for them and leave the old show_id / show_type standing in
  // the database. Send explicit nulls instead, so the type change actually clears
  // them.
  //
  // lang_id is the SHOW's language and belongs to a show banner only. It is not
  // what decides the language a viewer sees the banner in — `images` below is.
  if (payload.m_banner_type !== "show") {
    payload.show_type = null;
    payload.show_id = null;
    payload.lang_id = null;
  }

  // banner_date is stored as a UTC unix timestamp in milliseconds, so the picker's
  // dayjs object is converted to one here.
  //
  // Date.UTC of the picked year/month/day, NOT dayjs.valueOf(): valueOf() returns
  // local midnight, which in a zone ahead of UTC is the previous day in UTC — an
  // admin in Dubai picking the 15th would store the 14th. Taking the calendar
  // parts the admin actually saw and pinning them to UTC midnight makes the stored
  // day the picked day in every zone.
  // Cleared to null when the picker is emptied, which is what blanks the field.
  if (payload.banner_date && typeof payload.banner_date?.year === "function") {
    payload.banner_date = Date.UTC(
      payload.banner_date.year(),
      payload.banner_date.month(),
      payload.banner_date.date()
    );
  } else if (payload.banner_date) {
    // Already a timestamp — a value restored from the API rather than re-picked.
    const ms = Number(payload.banner_date);
    payload.banner_date = Number.isFinite(ms) ? ms : null;
  } else {
    payload.banner_date = null;
  }

  // The rail this banner belongs to and the id of what it stands for there.
  // Normalized to explicit nulls rather than passed through as the form left
  // them: antd hands back undefined for a field the admin cleared, JSON.stringify
  // drops an undefined key, and the edit endpoint leaves a column the request
  // never mentioned alone — so untagging a banner would never reach the database.
  payload.content_variant = payload.content_variant || null;
  const contentId = Number(payload.content_id);
  payload.content_id =
    Number.isInteger(contentId) && contentId > 0 ? contentId : null;

  // The per-language artwork, one entry per language. Always sent as an array,
  // even empty: the API leaves a banner's existing sets alone when the key is
  // absent, so omitting it would make "remove every image set" impossible.
  payload.images = (Array.isArray(payload.images) ? payload.images : [])
    // A set added but never given a language cannot be stored — lang_id is what
    // the read side matches on. The form's required rule blocks this before a
    // save, so it only guards against a value arriving another way.
    .filter((set) => set && set.lang_id)
    .map((set) => ({
      lang_id: set.lang_id,
      banner_image: set.banner_image || null,
      card_image: set.card_image || null,
      horizontal_movie_card_image: set.horizontal_movie_card_image || null,
      horizontal_sleek_banner_image: set.horizontal_sleek_banner_image || null,
      vertical_card_image: set.vertical_card_image || null,
    }));

  return payload;
};

/**
 * The banner as the API returns it, mapped onto the form fields. The artwork
 * fields are columns on the banner, so those names line up one to one.
 *
 */
export const bannerToFormValues = (banner) => ({
  desktop_image: banner?.desktop_image || "",
  mobile_image: banner?.mobile_image || "",
  horizontal_card_image: banner?.horizontal_card_image || "",
  redirect_url: banner?.redirect_url || "",
  // The banner's per-language artwork, as the API returns it. Every language the
  // banner already has comes back, so editing one of them does not drop the rest.
  images: (Array.isArray(banner?.images) ? banner.images : []).map((set) => ({
    lang_id: set?.lang_id,
    banner_image: set?.banner_image || "",
    card_image: set?.card_image || "",
    horizontal_movie_card_image: set?.horizontal_movie_card_image || "",
    horizontal_sleek_banner_image: set?.horizontal_sleek_banner_image || "",
    vertical_card_image: set?.vertical_card_image || "",
  })),
});

export default BannerLangImages;
